import type { Edge, Node } from '@xyflow/react';

import { branchColor, ROOT_EDGE_COLOR } from './mindmap-colors';
import type { MindmapData, MindmapLayout, MindmapNode } from './mindmap-types';

/**
 * MINDMAP PROTOTYPE — turn the model's flat node list into positioned
 * React Flow nodes + edges.
 *
 * TWO-SIDED tidy tree: the root sits in the middle and its branches fan out
 * LEFT and RIGHT (the classic mindmap shape), assigned by child parity —
 * even-indexed root children go right, odd go left. Parity (not leaf-count
 * balancing) on purpose: it's stable while the tree STREAMS in, so branches
 * never jump sides as new nodes arrive. Within a side it's the same tiny
 * hand-rolled layout as before: each leaf takes the next row and every parent
 * is vertically centred on its children.
 *
 * Each node also gets a `depth`, a `branchIndex` (which first-level branch
 * subtree it belongs to — drives the per-branch coloring, see
 * `mindmap-colors.ts`) and a `side` ('left' | 'right' — drives which side the
 * node's handles render on). Edges take the hue of their TARGET (child) node,
 * and edges leaving a root carry a `sourceHandle` ('left'/'right') because the
 * root renders one source handle per side.
 *
 * Defensive on purpose — the node list comes from the model, so we guard against
 * missing/duplicate ids, parents that don't exist, multiple roots, and cycles.
 */

export const X_GAP = 240; // horizontal distance between depth levels
export const Y_GAP = 72; // vertical distance between sibling rows

export type MindmapSide = 'left' | 'right';

const NODE_TYPE = 'mindmap'; // our editable custom node (see MindmapNode.tsx)

/** Smooth bezier edge tinted with its branch hue (no arrowhead — mindmap look). */
export function buildEdge(
  source: string,
  target: string,
  targetBranch: number,
  /** Set when the SOURCE is a root — roots have one source handle per side. */
  sourceHandle?: MindmapSide,
): Edge {
  return {
    id: `${source}->${target}`,
    source,
    target,
    ...(sourceHandle ? { sourceHandle } : {}),
    type: 'default',
    style: {
      stroke: targetBranch >= 0 ? branchColor(targetBranch).edge : ROOT_EDGE_COLOR,
      strokeWidth: 2,
    },
  };
}

/**
 * Branch color index per LOGICAL node id (root = -1, children of the root get
 * 0, 1, 2, … in list order, descendants inherit) — the same assignment the
 * layout produces, for callers that only have the flat `MindmapData` (e.g. the
 * deck split-source view tinting every grounded node's PDF range). Cycle-safe.
 */
export function branchIndexById(data: MindmapData): Map<string, number> {
  const input = (data?.nodes ?? []).filter(
    (n): n is MindmapNode => !!n && typeof n.id === 'string' && n.id.length > 0,
  );
  const byId = new Map(input.map((n) => [n.id, n]));
  const childrenOf = new Map<string, string[]>();
  const roots: string[] = [];
  for (const n of byId.values()) {
    const parent = n.parentId;
    if (parent && parent !== n.id && byId.has(parent)) {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(n.id);
      childrenOf.set(parent, arr);
    } else {
      roots.push(n.id);
    }
  }
  const out = new Map<string, number>();
  let nextBranch = 0;
  const queue = roots.map((id) => ({ id, depth: 0, branch: -1 }));
  while (queue.length > 0) {
    const { id, depth, branch } = queue.shift()!;
    if (out.has(id)) continue;
    out.set(id, branch);
    for (const child of childrenOf.get(id) ?? []) {
      queue.push({
        id: child,
        depth: depth + 1,
        branch: depth === 0 ? nextBranch++ : branch,
      });
    }
  }
  return out;
}

export function layoutMindmap(data: MindmapData): { nodes: Node[]; edges: Edge[] } {
  const flowNodes: Node[] = [];
  const flowEdges: Edge[] = [];

  const input = (data?.nodes ?? []).filter(
    (n): n is MindmapNode => !!n && typeof n.id === 'string' && n.id.length > 0,
  );
  if (input.length === 0) return { nodes: flowNodes, edges: flowEdges };

  // Index nodes by id (last write wins on duplicate ids), then build the
  // children map. A node whose parentId is missing/unknown/self is a root.
  const byId = new Map<string, MindmapNode>();
  for (const n of input) byId.set(n.id, n);

  const childrenOf = new Map<string, string[]>();
  const roots: string[] = [];
  for (const n of byId.values()) {
    const parent = n.parentId;
    if (parent && parent !== n.id && byId.has(parent)) {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(n.id);
      childrenOf.set(parent, arr);
    } else {
      roots.push(n.id);
    }
  }

  // Post-order walk: place children first, then centre the parent on them.
  // `visited` breaks any accidental cycle in the model's parentId references.
  // `nextBranch` hands each first-level subtree its own color index; each side
  // keeps its own leaf-row counter so left and right stack independently.
  const visited = new Set<string>();
  const nextLeafRow: Record<MindmapSide, number> = { left: 0, right: 0 };
  let nextBranch = 0;

  function place(id: string, depth: number, branch: number, side: MindmapSide): number {
    if (visited.has(id)) return nextLeafRow[side] * Y_GAP;
    visited.add(id);
    const node = byId.get(id)!;
    const children = (childrenOf.get(id) ?? []).filter((c) => !visited.has(c));

    let y: number;
    const childBranches: number[] = [];
    const childSides: MindmapSide[] = [];
    if (children.length === 0) {
      y = nextLeafRow[side] * Y_GAP;
      nextLeafRow[side] += 1;
    } else {
      const childYs = children.map((childId, i) => {
        // Children of a root each start a new colored branch, and alternate
        // sides by parity (stable while streaming). Deeper nodes inherit both.
        const childBranch = depth === 0 ? nextBranch++ : branch;
        const childSide: MindmapSide =
          depth === 0 ? (i % 2 === 0 ? 'right' : 'left') : side;
        childBranches.push(childBranch);
        childSides.push(childSide);
        return place(childId, depth + 1, childBranch, childSide);
      });
      // A root spans both sides — its own y is settled after both sides are
      // centred (see below), so seed it with the mean for now.
      y = (Math.min(...childYs) + Math.max(...childYs)) / 2;
    }

    flowNodes.push({
      id,
      type: NODE_TYPE,
      position: {
        x: depth === 0 ? 0 : side === 'right' ? depth * X_GAP : -depth * X_GAP,
        y,
      },
      data: { label: node.label ?? '', depth, branchIndex: branch, side },
    });
    children.forEach((childId, i) =>
      flowEdges.push(
        buildEdge(id, childId, childBranches[i], depth === 0 ? childSides[i] : undefined),
      ),
    );
    return y;
  }

  // Lay out each root in turn (normally one; extras stack below on the right).
  for (const rootId of roots) place(rootId, 0, -1, 'right');
  // Any node left unvisited (orphaned by a cycle) still gets placed as a root.
  for (const id of byId.keys()) if (!visited.has(id)) place(id, 0, -1, 'right');

  // Centre the two sides on each other: shift each side so its vertical middle
  // sits at 0, then drop every depth-0 node onto that midline. With a single
  // side this is a plain recentre and changes nothing visually.
  const sideMid = (side: MindmapSide): number | undefined => {
    const ys = flowNodes
      .filter((n) => n.data.side === side && n.data.depth !== 0)
      .map((n) => n.position.y);
    if (ys.length === 0) return undefined;
    return (Math.min(...ys) + Math.max(...ys)) / 2;
  };
  const midRight = sideMid('right');
  const midLeft = sideMid('left');
  for (const n of flowNodes) {
    if (n.data.depth === 0) continue;
    const mid = n.data.side === 'left' ? midLeft : midRight;
    if (mid !== undefined) n.position.y -= mid;
  }
  // Space stacked roots (the normal case is exactly one, at y 0).
  const rootNodes = flowNodes.filter((n) => n.data.depth === 0);
  rootNodes.forEach((n, i) => {
    n.position.y = i * Y_GAP;
  });

  return { nodes: flowNodes, edges: flowEdges };
}

/**
 * Build React Flow nodes/edges directly from a SAVED (user-edited) layout —
 * positions and labels are taken verbatim, so a reopened thread looks exactly
 * as the user left it. Depth / branch color assignments aren't persisted, so
 * they're re-derived from the saved edges (BFS from the roots); a node's SIDE
 * comes from where the user actually left it (x relative to the root).
 */
export function layoutFromSaved(layout: MindmapLayout): { nodes: Node[]; edges: Edge[] } {
  const savedNodes = layout?.nodes ?? [];
  const savedEdges = layout?.edges ?? [];

  // Roots = nodes that are never an edge target.
  const targets = new Set(savedEdges.map((e) => e.target));
  const childrenOf = new Map<string, string[]>();
  for (const e of savedEdges) {
    const arr = childrenOf.get(e.source) ?? [];
    arr.push(e.target);
    childrenOf.set(e.source, arr);
  }

  const rootX = savedNodes.find((n) => !targets.has(n.id))?.x ?? 0;
  const sideOf = (x: number): MindmapSide => (x < rootX ? 'left' : 'right');

  const meta = new Map<string, { depth: number; branchIndex: number }>();
  let nextBranch = 0;
  const queue: { id: string; depth: number; branch: number }[] = savedNodes
    .filter((n) => !targets.has(n.id))
    .map((n) => ({ id: n.id, depth: 0, branch: -1 }));
  while (queue.length > 0) {
    const { id, depth, branch } = queue.shift()!;
    if (meta.has(id)) continue; // guards cycles in a hand-edited payload
    meta.set(id, { depth, branchIndex: branch });
    for (const child of childrenOf.get(id) ?? []) {
      queue.push({
        id: child,
        depth: depth + 1,
        branch: depth === 0 ? nextBranch++ : branch,
      });
    }
  }

  const posById = new Map(savedNodes.map((n) => [n.id, n]));
  const nodes: Node[] = savedNodes.map((n) => {
    const m = meta.get(n.id);
    return {
      id: n.id,
      type: NODE_TYPE,
      position: { x: n.x, y: n.y },
      data: {
        label: n.label,
        depth: m?.depth ?? 0,
        branchIndex: m?.branchIndex ?? -1,
        side: m?.depth === 0 ? ('right' as const) : sideOf(n.x),
      },
    };
  });
  const edges: Edge[] = savedEdges.map((e) => {
    const sourceIsRoot = (meta.get(e.source)?.depth ?? 1) === 0;
    const built = buildEdge(
      e.source,
      e.target,
      meta.get(e.target)?.branchIndex ?? -1,
      sourceIsRoot ? sideOf(posById.get(e.target)?.x ?? 0) : undefined,
    );
    return { ...built, id: e.id };
  });
  return { nodes, edges };
}
